import * as Preact from 'preact';
import * as Hooks from 'preact/hooks';
import * as CardHelper from './A_CardHelper';
import * as CardGroup from './A_CardGroup';
import * as ConfigProductCard from './A_ConfigProductCard';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import * as ProgressGroupWithParagraph from './O_ProgressGroupWithParagraph';
import Box from '@mui/material/Box';

/**
 * Figma design nodes:
 * Mobile: https://www.figma.com/file/va7snk88pL7TR7F75h7zbt/Design-Files?node-id=9687%3A59119
 * Desktop: https://www.figma.com/file/va7snk88pL7TR7F75h7zbt/Design-Files?node-id=9687%3A59368
 */

export type Props = {
    products: Array<ConfigProductCard.Props>;
    helperText: string;
    nullOptionText: string;
    onSelectOption: (propertyValue: string | number | null) => void;
    onBecomeVisible: (propertyValue: string | number | null) => void;
    loading: boolean;
    statusText: string;
    loadingText: string;
};

export const Component = (p: Props) => {
    const compatible = p.products.filter(product => !!product.compatible);
    const incompatible = p.products.filter(product => !product.compatible);

    return p.loading ? (
        <Box
            height="100%"
            display="flex"
            flexDirection="column"
            sx={{
                justifyContent: { md: 'center' },
                paddingX: '16px',
                paddingY: { xs: '16px', md: '0px' },
            }}
        >
            <ProgressGroupWithParagraph.Component
                paragraph={p.loadingText}
                statusText={p.statusText}
            />
        </Box>
    ) : (
        <CardGroup.Component>
            {compatible.map(c => (
                <WatchVisibility
                    onBecomeVisible={() => {
                        p.onBecomeVisible(c.propertyValue);
                    }}
                >
                    <ListItemButton
                        sx={{
                            width: 'min-content',
                            borderRadius: '8px',
                            flexGrow: 0,
                            padding: '0px',
                        }}
                        autoFocus={c.active}
                        onClick={() => p.onSelectOption(c.propertyValue)}
                        data-testId={c.active ? undefined : 'option-btn'}
                    >
                        <ConfigProductCard.Component {...c} nullOptionText={p.nullOptionText} />
                    </ListItemButton>
                </WatchVisibility>
            ))}
            {incompatible.length > 0 && (
                <ListItem disableGutters disablePadding sx={{ width: 'auto' }}>
                    <CardHelper.Component helperText={p.helperText} />
                </ListItem>
            )}
            {incompatible.map(i => (
                <WatchVisibility
                    onBecomeVisible={() => {
                        p.onBecomeVisible(i.propertyValue);
                    }}
                >
                    <ListItemButton
                        sx={{
                            width: 'min-content',
                            borderRadius: '8px',
                            flexGrow: 0,
                            padding: '0px',
                        }}
                        onClick={() => p.onSelectOption(i.propertyValue)}
                    >
                        <ConfigProductCard.Component {...i} nullOptionText={p.nullOptionText} />
                    </ListItemButton>
                </WatchVisibility>
            ))}
        </CardGroup.Component>
    );
};

const WatchVisibility = (p: {
    children: Preact.ComponentChildren;
    onBecomeVisible: () => void;
}) => {
    const { visible, updateRef } = useVisibility();
    Hooks.useEffect(() => {
        if (visible) {
            p.onBecomeVisible();
        }
    }, [visible, p.onBecomeVisible]);
    return <Box ref={updateRef}>{p.children}</Box>;
};

function useVisibility(): { visible: boolean; updateRef: Preact.RefCallback<Element> } {
    const [element, setElement] = Hooks.useState<null | Element>(null);
    const [visible, setVisible] = Hooks.useState<boolean>(false);
    const observer = Hooks.useMemo(() => {
        const callback = (
            entries: IntersectionObserverEntry[],
            _observer: IntersectionObserver,
        ) => {
            entries.forEach(entry => {
                setVisible(entry.isIntersecting);
            });
        };
        return new IntersectionObserver(callback, { root: null, threshold: 0.1 });
    }, []);

    Hooks.useEffect(() => {
        if (!element) return;
        observer.observe(element);
        return () => {
            observer.unobserve(element);
        };
    }, [element]);

    const updateRef = Hooks.useCallback((e: null | Element) => {
        if (e === null) return;
        setElement(e);
    }, []);

    return {
        visible,
        updateRef,
    };
}
